class ArcaPartnerMarquee {
  constructor(marquee) {
    this.marquee = marquee;
    this.section = marquee.closest('.arca-partner-network');
    this.track = marquee.querySelector('[data-partner-track]');
    this.sequence = this.track?.querySelector('[data-partner-sequence]');
    this.offset = 0;
    this.lastFrameTime = 0;
    this.frameId = null;
    this.isInView = false;
    this.isHovered = false;
    this.hasFocus = false;

    if (!this.section || !this.track || !this.sequence) return;

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.animate = this.animate.bind(this);
    this.resize = this.resize.bind(this);

    this.marquee.addEventListener('pointerenter', this.handlePointerEnter, { passive: true });
    this.marquee.addEventListener('pointerleave', this.handlePointerLeave, { passive: true });
    this.marquee.addEventListener('focusin', this.handleFocusIn);
    this.marquee.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.resize);
      this.resizeObserver.observe(this.marquee);
      this.resizeObserver.observe(this.sequence);
    } else {
      window.addEventListener('resize', this.resize, { passive: true });
    }

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.isInView = entries.some((entry) => entry.isIntersecting);
        this.updatePlayback();
      });
      this.intersectionObserver.observe(this.marquee);
    } else {
      this.isInView = true;
    }

    this.resize();
  }

  get speed() {
    const duration = Number.parseFloat(
      window.getComputedStyle(this.section).getPropertyValue('--arca-partner-marquee-duration'),
    );
    const durationMs = (Number.isFinite(duration) && duration > 0 ? duration : 28) * 1000;
    return this.distance / durationMs;
  }

  handleVisibilityChange() {
    this.updatePlayback();
  }

  handlePointerEnter(event) {
    if (event.pointerType === 'touch') return;
    this.isHovered = true;
    this.updatePlayback();
  }

  handlePointerLeave(event) {
    if (event.pointerType === 'touch') return;
    this.isHovered = false;
    this.updatePlayback();
  }

  handleFocusIn() {
    this.hasFocus = true;
    this.updatePlayback();
  }

  handleFocusOut(event) {
    if (this.marquee.contains(event.relatedTarget)) return;
    this.hasFocus = false;
    this.updatePlayback();
  }

  resize() {
    if (!this.sequence?.isConnected) return;

    const distance = this.sequence.getBoundingClientRect().width;
    const viewportWidth = this.marquee.clientWidth;
    if (!distance || !viewportWidth) return;

    this.track.querySelectorAll('[data-partner-generated-sequence]').forEach((sequence) => sequence.remove());
    this.track.querySelectorAll('[data-partner-sequence]:not(:first-child)').forEach((sequence) => sequence.remove());

    const copyCount = Math.max(2, Math.ceil(viewportWidth / distance) + 1);
    for (let index = 1; index < copyCount; index += 1) {
      const clone = this.sequence.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-partner-generated-sequence', '');
      clone.removeAttribute('data-partner-sequence');
      [clone, ...clone.querySelectorAll('[id], [data-shopify-editor-block], [data-block-id]')].forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
        element.removeAttribute('data-block-id');
      });
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      this.track.appendChild(clone);
    }

    this.distance = distance;
    this.offset %= distance;
    this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
    this.updatePlayback();
  }

  updatePlayback() {
    const shouldPlay =
      this.isInView &&
      !document.hidden &&
      !this.isHovered &&
      !this.hasFocus &&
      this.distance > 0;

    if (!shouldPlay) {
      if (this.frameId !== null) window.cancelAnimationFrame(this.frameId);
      this.frameId = null;
      this.lastFrameTime = 0;
      this.track.style.removeProperty('will-change');
      return;
    }

    if (this.frameId === null) {
      this.track.style.willChange = 'transform';
      this.frameId = window.requestAnimationFrame(this.animate);
    }
  }

  animate(timestamp) {
    this.frameId = null;
    if (document.hidden || !this.isInView || this.isHovered || this.hasFocus || !this.distance) return;

    if (this.lastFrameTime) {
      const elapsed = Math.min(timestamp - this.lastFrameTime, 64);
      this.offset = (this.offset + this.speed * elapsed) % this.distance;
      this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
    }

    this.lastFrameTime = timestamp;
    this.frameId = window.requestAnimationFrame(this.animate);
  }

  destroy() {
    if (this.frameId !== null) window.cancelAnimationFrame(this.frameId);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.marquee.removeEventListener('pointerenter', this.handlePointerEnter);
    this.marquee.removeEventListener('pointerleave', this.handlePointerLeave);
    this.marquee.removeEventListener('focusin', this.handleFocusIn);
    this.marquee.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('resize', this.resize);
    this.track?.querySelectorAll('[data-partner-generated-sequence]').forEach((sequence) => sequence.remove());
    this.track?.style.removeProperty('transform');
    this.track?.style.removeProperty('will-change');
  }
}

const partnerMarquees = new WeakMap();

function initPartnerMarquees(root = document) {
  const marquees = [
    ...(root.matches?.('[data-partner-marquee]') ? [root] : []),
    ...root.querySelectorAll('[data-partner-marquee]'),
  ];

  marquees.forEach((marquee) => {
    if (partnerMarquees.has(marquee)) return;
    const instance = new ArcaPartnerMarquee(marquee);
    if (instance.track && instance.sequence) partnerMarquees.set(marquee, instance);
  });
}

function destroyPartnerMarquees(root) {
  const marquees = [
    ...(root.matches?.('[data-partner-marquee]') ? [root] : []),
    ...root.querySelectorAll('[data-partner-marquee]'),
  ];

  marquees.forEach((marquee) => {
    partnerMarquees.get(marquee)?.destroy();
    partnerMarquees.delete(marquee);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPartnerMarquees(), { once: true });
} else {
  initPartnerMarquees();
}

document.addEventListener('shopify:section:load', (event) => initPartnerMarquees(event.target));
document.addEventListener('shopify:section:unload', (event) => destroyPartnerMarquees(event.target));
